import React, { useEffect, useState } from "react";
import { BookOpen, Plus, Pencil, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty, StatusPill } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function MenuRecipes() {
  const { user } = useAuth();
  const canEdit = ["admin", "kitchen"].includes(user.role);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [items, setItems] = useState(null);
  const [ings, setIngs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/brands").then((r) => { setBrands(r.data); setBrandId(r.data[0]?.id || ""); });
    api.get("/ingredients").then((r) => setIngs(r.data)).catch(() => {});
  }, []);

  const loadItems = (bid) => api.get(`/menu-items?brand_id=${bid}`).then((r) => setItems(r.data));
  useEffect(() => { if (brandId) loadItems(brandId); }, [brandId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ brand_id: brandId, name: "", sku: "", category: "Main", selling_price: 0, active: true, recipe: [] }); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ brand_id: m.brand_id, name: m.name, sku: m.sku, category: m.category, selling_price: m.selling_price, active: m.active, recipe: (m.recipe_detail || []).map((l) => ({ ingredient_id: l.ingredient_id, qty: l.qty })) }); setEditId(m.id); setOpen(true); };

  const addLine = () => set("recipe", [...form.recipe, { ingredient_id: ings[0]?.id, qty: 0 }]);
  const updLine = (idx, k, v) => set("recipe", form.recipe.map((l, i) => (i === idx ? { ...l, [k]: v } : l)));
  const rmLine = (idx) => set("recipe", form.recipe.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, selling_price: Number(form.selling_price), recipe: form.recipe.map((l) => ({ ingredient_id: l.ingredient_id, qty: Number(l.qty) })) };
      if (editId) await api.put(`/menu-items/${editId}`, payload);
      else await api.post("/menu-items", payload);
      toast.success(editId ? "Menu item updated" : "Menu item created");
      setOpen(false); loadItems(brandId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/menu-items/${id}`); toast.success("Deleted"); loadItems(brandId); };

  return (
    <div>
      <PageHeader title="Menu & Recipes" subtitle="Per-brand menu items with auto-calculated recipe costs">
        {canEdit && brandId && (
          <Button onClick={openNew} data-testid="add-menu-item-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
            <Plus className="h-4 w-4" /> New Item
          </Button>
        )}
      </PageHeader>

      <div className="max-w-xs mb-6">
        <SelectBox value={brandId} onChange={setBrandId} options={brands.map((b) => ({ value: b.id, label: b.name }))} testid="menu-brand-select" />
      </div>

      {!items ? <Spinner /> : items.length === 0 ? <Empty icon={BookOpen} title="No menu items" hint={canEdit ? "Add this brand's menu items." : undefined} /> : (
        <div className="space-y-3">
          {items.map((m) => {
            const open = expanded[m.id];
            return (
              <div key={m.id} className="card-surface overflow-hidden" data-testid={`menu-item-${m.id}`}>
                <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))}>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white truncate">{m.name}</p>
                      {!m.active && <StatusPill status="Inactive" />}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{m.sku || "—"} · {m.category}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] uppercase text-muted-foreground font-mono">Price</p>
                    <p className="font-mono text-white">{money(m.selling_price)}</p>
                  </div>
                  {m.client_cost != null && (
                    <div className="text-right shrink-0 hidden sm:block">
                      <p className="text-[10px] uppercase text-muted-foreground font-mono">Client Cost</p>
                      <p className="font-mono text-amber-300">{money(m.client_cost)}</p>
                    </div>
                  )}
                  {canEdit && (
                    <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`edit-menu-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(m.id)} data-testid={`delete-menu-${m.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                    </div>
                  )}
                </div>
                {open && (
                  <div className="border-t border-border p-4 bg-secondary/20">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-2">Recipe / BOM</p>
                    <div className="space-y-1.5">
                      {(m.recipe_detail || []).map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-slate-200">{l.ingredient_name} — <span className="font-mono">{num(l.qty)}{l.consumption_unit}</span></span>
                          {l.client_cost != null && <span className="font-mono text-amber-300/90">{money(l.client_cost)}</span>}
                        </div>
                      ))}
                      {(m.recipe_detail || []).length === 0 && <p className="text-sm text-muted-foreground">No recipe defined.</p>}
                    </div>
                    <div className="flex flex-wrap gap-6 mt-3 pt-3 border-t border-border text-sm">
                      {m.internal_cost != null && <span className="text-slate-300">Internal cost <b className="font-mono text-white">{money(m.internal_cost)}</b></span>}
                      {m.client_cost != null && <span className="text-slate-300">Client cost <b className="font-mono text-amber-300">{money(m.client_cost)}</b></span>}
                      {m.margin_pct != null && <span className="text-slate-300">Margin <b className="font-mono text-emerald-400">{m.margin_pct}%</b></span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader><DialogTitle>{editId ? "Edit Menu Item" : "New Menu Item"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldW label="Item name" full><Input data-testid="menu-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></FieldW>
              <FieldW label="SKU"><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} /></FieldW>
              <FieldW label="Category"><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></FieldW>
              <FieldW label="Selling price (AED)"><Input type="number" data-testid="menu-price" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} /></FieldW>
              <FieldW label="Active"><SelectBox value={form.active ? "Active" : "Inactive"} onChange={(v) => set("active", v === "Active")} options={["Active", "Inactive"]} /></FieldW>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-white">Recipe lines</p>
                <Button variant="outline" size="sm" onClick={addLine} data-testid="add-recipe-line" className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add ingredient</Button>
              </div>
              <div className="space-y-2">
                {form.recipe.map((l, i) => {
                  const ing = ings.find((x) => x.id === l.ingredient_id);
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1"><SelectBox value={l.ingredient_id} onChange={(v) => updLine(i, "ingredient_id", v)} options={ings.map((x) => ({ value: x.id, label: x.name }))} /></div>
                      <Input type="number" value={l.qty} onChange={(e) => updLine(i, "qty", e.target.value)} className="w-24" data-testid={`recipe-qty-${i}`} />
                      <span className="text-xs text-muted-foreground w-8">{ing?.consumption_unit}</span>
                      <Button variant="ghost" size="icon" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                    </div>
                  );
                })}
                {form.recipe.length === 0 && <p className="text-sm text-muted-foreground">No ingredients added yet.</p>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !form.name} data-testid="save-menu-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
